// Bottom-of-landing-page cross-sell strip. Surfaces the OTHER 5 ebooks
// (everything in the catalog except the one currently being viewed) so
// every visitor to one landing is one click from the other five.
//
// Mounted by every ebook landing: GettingStartedLanding, EbookLandingTemplate
// (W2→$10K, Black Book, Founder Restart), TcpaGuide, and FreeEbook.

import { Link } from "react-router-dom";
import { EBOOK_CATALOG } from "@/lib/ebookCatalog";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import { ArrowRight, BookOpen } from "lucide-react";

export default function MoreEbooksStrip({ currentSlug }) {
  const others = EBOOK_CATALOG.filter((e) => e.slug !== currentSlug);
  return (
    <section
      className="bg-[var(--bg)] py-16 lg:py-20 border-t border-[var(--line)]"
      data-testid="more-ebooks-strip"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <div className="mkt-eyebrow">More free guides</div>
            <h2 className="mkt-h2 max-w-2xl">
              Don&apos;t stop here. The library is free.
            </h2>
          </div>
          <Link
            to="/library"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-[var(--primary-dark)] hover:text-[var(--ink)] transition flex-shrink-0"
            data-testid="more-ebooks-view-all"
          >
            View the full library <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {others.map((e) => (
            <Link
              key={e.slug}
              to={e.landingPath}
              className="group relative bg-white rounded-2xl border border-[var(--line)] overflow-hidden hover:border-[var(--primary)]/60 hover:-translate-y-0.5 transition"
              data-testid={`more-ebook-${e.slug}`}
            >
              {/* Mini cover */}
              <div
                className="relative aspect-[1.6/1] overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg, #0B1220 0%, #0F172A 60%, #0B1220 100%)",
                }}
              >
                <div
                  className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-50"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(16,185,129,0.4), transparent 60%)",
                  }}
                />
                <div className="absolute inset-y-0 left-0 w-1 bg-[var(--primary)]" />
                <div className="absolute inset-0 p-3 flex flex-col">
                  <div className="flex items-center gap-1.5 text-white/80">
                    <span className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center">
                      <AssignFlowLogo size={10} />
                    </span>
                    <span className="font-display font-bold text-[10px]">
                      iSpeedToClose
                    </span>
                  </div>
                  <div className="mt-auto">
                    <div className="text-[8px] font-bold tracking-[0.16em] text-[var(--primary)] mb-1.5">
                      {e.category.toUpperCase()} · {e.pageCount} PP
                    </div>
                    <div className="font-display font-black text-white tracking-tight leading-[0.95] text-[15px]">
                      {e.title}
                    </div>
                  </div>
                </div>
              </div>
              {/* Card body */}
              <div className="p-4">
                <div className="text-xs text-[var(--ink-2)] leading-snug min-h-[2.6rem]">
                  {e.tagline}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-[var(--primary-dark)] group-hover:gap-2 transition-all">
                  <BookOpen size={12} /> Read the guide{" "}
                  <ArrowRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 sm:hidden text-center">
          <Link
            to="/library"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--primary-dark)]"
          >
            View the full library <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
